import {
  TagCloud as FMCloud,
  type TagCloudOptions,
} from '@frank-mayer/react-tag-cloud';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';

import ResumeSection from '../../components/ResumeSection';
import TagCloud from '../../components/TagCloud';

import styles from './Resume.module.scss';

// const tagColor = 'var(--brand-color-1, #0572ce)';
const skillMatrix = [
  { value: 'Javascript', score: 80 },
  { value: 'React', score: 60 },
  { value: 'CSS', score: 80 },
  { value: 'SASS', score: 65 },
  { value: 'Github', score: 50 },
  { value: 'ES6', score: 50 },
  { value: 'HTML', score: 80 },
  { value: 'Testing Frameworks', score: 50 },
  { value: 'AI coding tools', score: 50 },
  { value: 'Communication', score: 90 },
  { value: 'Java', score: 25 },
  { value: 'Agile Development', score: 90 },
];

interface ResumeSectionData {
  eventKey: string;
  title: string;
  type: 'html' | 'skills';
  content?: React.ReactNode;
}

const Resume = () => {
  const [resumeData, setResumeData] = useState<ResumeSectionData[]>([]);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        const resp = await axios.get<ResumeSectionData[]>(
          '/json/resumeData.json',
        );
        // Normalize response to an array in case the JSON is an object map
        let data: ResumeSectionData[] = [];
        if (Array.isArray(resp.data)) data = resp.data;
        else if (resp.data && typeof resp.data === 'object')
          data = Object.values(resp.data) as ResumeSectionData[];

        // Debugging: log fetched length to help diagnose partial rendering

        console.debug('resume fetch', { length: data.length });

        if (!cancelled) setResumeData(data);
      } catch (err) {
        console.error('Failed to load resume data', err);
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, []);

  const name = 'Hal Barnett';
  const [activeKey, setActiveKey] = useState<string | string[] | null>(['0']);

  const headerRefs = useRef<(HTMLDivElement | null)[]>([]);

  const renderSectionContent = (section: ResumeSectionData) => {
    if (section.type === 'skills') {
      return (
        <>
          <div>
            <FMCloud
              options={(w: Window & typeof globalThis): TagCloudOptions => ({
                radius: Math.min(350, w.innerWidth, w.innerHeight) / 2,
                maxSpeed: 'fast',
              })}
              className="oba-tag-cloud"
            >
              {skillMatrix.map((tag) => tag.value).concat(name)}
            </FMCloud>
          </div>
          <div>
            <TagCloud
              minSize={0.75}
              maxSize={1.5}
              tags={skillMatrix}
              baseColor="var(--brand-color-2)"
              order="random"
              placement="top"
            />
          </div>
        </>
      );
    }
    // When content comes from JSON we expect HTML strings for 'html' type
    if (typeof section.content === 'string') {
      return <div dangerouslySetInnerHTML={{ __html: section.content }} />;
    }
    return section.content;
  };

  const checkKeys = (key: string) => {
    const isActiveKeyArray = Array.isArray(activeKey);
    if (activeKey === null || (Array.isArray(activeKey) && !activeKey.length))
      return [key];
    if (isActiveKeyArray && !activeKey.includes(key)) {
      return [...activeKey, key];
    }
    return isActiveKeyArray ? activeKey : [activeKey];
  };

  const handleKeyChange = (key: string) => {
    const newKeys = checkKeys(key);
    if (newKeys.length) {
      const headerEl = headerRefs.current[Number(key)];
      if (headerEl) {
        setTimeout(() => {
          headerEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setTimeout(() => {
            headerEl
              .querySelector<HTMLButtonElement>('.accordion-button')
              ?.focus();
          }, 700);
        }, 350);
      }
    }
    setActiveKey(newKeys);
  };

  return (
    <div>
      <h2 className="mb-4">Resume for {name}</h2>
      <Accordion
        alwaysOpen
        activeKey={activeKey}
        onSelect={(key) => setActiveKey(key ?? null)}
        className={styles['accordion']}
      >
        {resumeData.map((section, index) => {
          const prevSection = resumeData[index - 1];
          const nextSection = resumeData[index + 1];

          return (
            <ResumeSection
              key={section.eventKey}
              eventKey={section.eventKey}
              title={section.title}
              onNavigate={handleKeyChange}
              prevButtonText={prevSection?.title}
              prevEventKey={prevSection?.eventKey}
              nextButtonText={nextSection?.title}
              nextEventKey={nextSection?.eventKey}
              headerRef={(el) => (headerRefs.current[index] = el)}
            >
              {renderSectionContent(section)}
            </ResumeSection>
          );
        })}
      </Accordion>
    </div>
  );
};

export default Resume;
